import React, { memo, useState } from 'react'
import { View, Text } from 'react-native'
import { Image } from 'expo-image'
import { LinearGradient } from 'expo-linear-gradient'
import { useTranslation } from 'react-i18next'
import { Pressable } from '../ui/Pressable'
import { Rating } from '../ui/Rating'
import { Skeleton } from '../ui/Skeleton'
import { useSemanticColors, RADIUS, SPACING } from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'
import { COLORS, FONTS } from '../../lib/brand'
import { IMAGE_CACHE_POLICY, IMAGE_TRANSITION_MS } from '../../lib/utils/image-cache'
import { usePressAction } from '../../lib/hooks/use-press-action'
import { getVehicleDisplayPriceCents } from '../../lib/utils/pricing'
import { formatPrice } from '../../lib/utils/format'
import { marketTransmissionI18nKey } from '../../lib/utils/transmission'
import { GlassSurface } from '../ui/GlassSurface'
import type { SearchVehicle } from '../../lib/types/vehicle.types'

interface VehicleListItemProps {
  vehicle: SearchVehicle
  onPress: () => void
  desktop?: boolean
  /** Map groups can contain several cars of the same model; keep their full title readable. */
  showFullTitle?: boolean
}

function VehicleListItemBase({ vehicle, onPress, desktop, showFullTitle }: VehicleListItemProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  // The map's first-tap list is often the recovery path after a weak network.
  // A remote image failure must not turn that decision surface into a broken
  // thumbnail. Keeping the URL, not a boolean reset by an effect, means a
  // recycled row renders the new photo in the same frame.
  const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null)
  const imageFailed = vehicle.primaryImageUrl !== null && failedImageUrl === vehicle.primaryImageUrl
  // Sticker routed through the shared helper (lib/utils/pricing.ts) so
  // this number is identical to the map pin, vehicle detail, and booking
  // intro. One edit there re-skins every sticker.
  const sticker = getVehicleDisplayPriceCents(vehicle)
  const price = formatPrice(sticker.cents)
  const rateLabel =
    sticker.unit === 'hour' ? t('vehicle_detail.per_hour') : t('vehicle_detail.per_day')
  const transmissionLabelKey = marketTransmissionI18nKey(
    'explore.transmission',
    vehicle.transmission,
  )

  const thumbW = desktop ? 120 : 95
  const thumbH = desktop ? 90 : 75
  const titleSize = desktop ? 15 : 13
  const priceSize = desktop ? 18 : 15
  // The two most important values in the row — the car and its price — sit on
  // one line. Both columns start at the top and both first lines share this
  // line box, so the price no longer floats in the gap between the title and
  // the rating row on the left.
  const headlineLineHeight = desktop ? 24 : 20
  const handlePress = usePressAction(onPress)
  const title = showFullTitle ? vehicle.title : `${vehicle.makeName} ${vehicle.modelName}`

  return (
    <Pressable
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={`${title}, ${price}${rateLabel}`}
      testID="explore.vehicle-list-item"
      style={{ borderRadius: RADIUS.lg }}
    >
      <GlassSurface
        tone="regular"
        radius={RADIUS.lg}
        contentStyle={{
          flexDirection: 'row',
          gap: desktop ? 14 : 11,
          padding: desktop ? 12 : 7,
        }}
      >
        <View
          style={{ width: thumbW, height: thumbH, borderRadius: RADIUS.md, overflow: 'hidden' }}
        >
          {vehicle.primaryImageUrl && !imageFailed ? (
            <Image
              source={vehicle.primaryImageUrl}
              // FlashList recicla esta celda: sin clave, la imagen del ítem
              // anterior puede verse un frame (flicker visible en equipos lentos).
              recyclingKey={vehicle.id}
              placeholder={
                vehicle.primaryImageBlurhash
                  ? { blurhash: vehicle.primaryImageBlurhash }
                  : undefined
              }
              placeholderContentFit={vehicle.primaryImageBlurhash ? 'cover' : undefined}
              style={{ width: '100%', height: '100%' }}
              contentFit="cover"
              transition={IMAGE_TRANSITION_MS}
              cachePolicy={IMAGE_CACHE_POLICY}
              onError={() => setFailedImageUrl(vehicle.primaryImageUrl)}
            />
          ) : (
            <LinearGradient
              colors={
                isDark
                  ? ['#1E1A38', '#2A1F4E']
                  : [`${COLORS.purple.DEFAULT}12`, `${COLORS.coral.DEFAULT}10`]
              }
              style={{
                width: '100%',
                height: '100%',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Text
                style={{
                  fontSize: 18,
                  color: isDark ? 'rgba(255,255,255,0.06)' : colors.textMuted,
                  opacity: isDark ? 1 : 0.12,
                  fontFamily: FONTS.body,
                  letterSpacing: -1,
                }}
              >
                {vehicle.makeName}
              </Text>
            </LinearGradient>
          )}
        </View>

        <View style={{ flex: 1, justifyContent: 'flex-start' }}>
          <Text
            style={{
              fontSize: titleSize,
              lineHeight: headlineLineHeight,
              color: colors.textPrimary,
              fontFamily: FONTS.bodyBold,
              letterSpacing: -0.2,
            }}
            numberOfLines={showFullTitle ? 2 : 1}
          >
            {title}
          </Text>
          {/* This row used to shrink on the small screen and grow on the large
            one — 10.5px and 10px on a phone against 12px and 11px on desktop.
            Backwards: the phone is where the rating is hardest to read and
            where it is read most. 12px is the floor CLAUDE.md sets, so both
            breakpoints sit at or above it now. */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3, marginTop: 3 }}>
            {(vehicle.ratingCount ?? 0) > 0 ? (
              <>
                <Rating value={vehicle.ratingAvg} size={desktop ? 11 : 10} />
                <Text
                  style={{
                    fontSize: 12,
                    color: colors.textPrimary,
                    fontFamily: FONTS.body,
                    fontVariant: ['tabular-nums'],
                  }}
                >
                  {(vehicle.ratingAvg ?? 0).toFixed(1)}
                </Text>
              </>
            ) : null}
            {(vehicle.rentedDays ?? 0) > 0 ? (
              // Los días rentados se muestran SIEMPRE que existan, con o sin
              // reseñas: es la métrica de prueba social, y un carro recién
              // alquilado que todavía no tiene reseña no debería verse "Nuevo".
              <Text
                style={{
                  fontSize: 12,
                  color: colors.textSecondary,
                  fontFamily: FONTS.body,
                }}
                numberOfLines={1}
              >
                {(vehicle.ratingCount ?? 0) > 0 ? '· ' : ''}
                {t('explore.rented_days', { count: vehicle.rentedDays })}
              </Text>
            ) : (vehicle.ratingCount ?? 0) === 0 ? (
              // See VehicleCard: no reviews is "Nuevo", not zero stars.
              <Text style={{ fontSize: 12, color: colors.textBrand, fontFamily: FONTS.bodyBold }}>
                {t('explore.rating_new')}
              </Text>
            ) : null}
          </View>
          <View style={{ flexDirection: 'row', gap: SPACING.xs, marginTop: SPACING.xs + 1 }}>
            <View
              style={{
                paddingHorizontal: SPACING.sm - 1,
                paddingVertical: 3,
                borderRadius: RADIUS.sm,
                backgroundColor: isDark ? colors.surfaceRaised : colors.background,
              }}
            >
              <Text
                style={{
                  fontSize: 12,

                  color: colors.textSecondary,
                  fontFamily: FONTS.body,
                }}
              >
                {transmissionLabelKey ? t(transmissionLabelKey) : ''}
              </Text>
            </View>
          </View>
        </View>

        <View
          style={{
            justifyContent: 'flex-start',
            alignItems: 'flex-end',
            paddingRight: SPACING.sm - 2,
            maxWidth: 130,
          }}
        >
          <Text
            numberOfLines={1}
            adjustsFontSizeToFit
            minimumFontScale={0.8}
            style={{
              fontSize: priceSize,
              lineHeight: headlineLineHeight,
              color: colors.textPrimary,
              fontFamily: FONTS.bodyBold,
              letterSpacing: -0.3,
              fontVariant: ['tabular-nums'],
            }}
          >
            {price}
          </Text>
          <Text style={{ fontSize: 12, color: colors.textSecondary, fontFamily: FONTS.body }}>
            {rateLabel}
          </Text>
        </View>
      </GlassSurface>
    </Pressable>
  )
}

function VehicleListItemSkeleton({ desktop }: { desktop?: boolean }) {
  return (
    <View style={{ flexDirection: 'row', gap: SPACING.md - 5, padding: SPACING.sm - 1 }}>
      <Skeleton width={desktop ? 120 : 95} height={desktop ? 90 : 75} borderRadius={RADIUS.md} />
      <View style={{ flex: 1, justifyContent: 'center' }}>
        <Skeleton.Text lines={2} lastLineWidth="50%" />
      </View>
    </View>
  )
}

const MemoizedVehicleListItem = memo(VehicleListItemBase)
MemoizedVehicleListItem.displayName = 'VehicleListItem'

export const VehicleListItem = Object.assign(MemoizedVehicleListItem, {
  Skeleton: VehicleListItemSkeleton,
})
